import { get } from 'lodash';
import STEP_ROUTES from '../../../constants/activation-brief-step-routes';
import ActivationBriefMixin from './ActivationBriefMixin';
import { ACTIVATION_BRIEF_STATUS_NONE } from '../../../constants/activation-brief';
import { STEP_ORDERS } from '../../../constants/activation-brief-step-orders';
import { ACTIVATION_TYPE_CUSTOM } from '../../../constants/activation-task';
import { NAME_NETWORKS, NAME_TASKS } from '../../../constants/activation-brief-steps';
import { ANALYTICS_PROPOSAL_STEP_EVENTS } from '../../../constants/analytics-events';
import EventTrackingMixin from '../../../mixins/EventTrackingMixin';

export default {
    mixins: [ActivationBriefMixin, EventTrackingMixin],
    props: {
        isAdditionalOrder: {
            type: Boolean,
            default: false
        }
    },
    computed: {
        stepsOrder() {
            return get(this.$root.v2, 'brief.module_progress', []);
        },
        briefStatus() {
            return get(this.$root.v2, 'brief.status', ACTIVATION_BRIEF_STATUS_NONE);
        },
        steps() {
            const steps = get(this.$root.v2, 'brief.module_progress', []);

            // Remove networks step if the task only has custom tasks
            if (this.isOnlyCustomTasks) {
                const index = steps.findIndex((step) => step.name === NAME_NETWORKS);
                if (index > -1) {
                    steps.splice(index, 1);
                }
            }

            // Remove tasks step if there are not tasks AND no hashtags/mentions.
            if (!this.hasTasks && !this.hasHashtags) {
                const index = steps.findIndex((step) => step.name === NAME_TASKS);
                if (index > -1) {
                    steps.splice(index, 1);
                }
            }

            const totalSteps = steps.length;

            return steps.reduce((result, step, index) => {
                const meta = {
                    meta: {
                        stepNumber: index + 1,
                        totalSteps
                    }
                };

                result[step.name] = {
                    ...step,
                    ...meta
                };
                return result;
            }, {});
        },
        orderedSteps() {
            return STEP_ORDERS[this.briefStatus]
                .filter((step) => get(this.steps, step, false))
                .map((stepName) => this.steps[stepName]);
        },
        currentStep() {
            const currentStep = this.orderedSteps.find((step) => !step.is_complete) || {};
            return this.steps[currentStep.name];
        },
        isPreview() {
            return !!this.$route.query.preview;
        },
        isOnlyCustomTasks() {
            const tasks = Array.isArray(this.brief?.tasks) ? this.brief.tasks : [];
            if (tasks.length === 0) {
                return false;
            }

            const selectedOrRequiredTasks = tasks.filter(
                (task) => task.is_required === true || task.is_selected === true
            );

            return selectedOrRequiredTasks.every((task) => task.type === ACTIVATION_TYPE_CUSTOM);
        },
        hasTasks() {
            return Array.isArray(this.brief?.tasks) && this.brief.tasks.length > 0;
        },
        hasHashtags() {
            return Array.isArray(this.brief?.tracking_tags) && this.brief.tracking_tags.length > 0;
        }
    },
    methods: {
        getStepUrl(name, complete = false) {
            const tail = complete ? '/complete' : '';
            return `/api/v1/activation-briefs/${this.$route.params.brief_id}/steps/${name}${tail}`;
        },
        postStep(data) {
            return this.$http.post(this.getStepUrl(data.step), data.payload).then((response) => {
                this.updateStep(response.data);
            });
        },
        patchStep(data) {
            this.isPatchingBrief = true;
            this.resetErrors();

            if (typeof this.updateInitialStepState === 'function') {
                this.updateInitialStepState();
            }

            return this.$http
                .patch(this.getStepUrl(data.step, data.complete), data.payload)
                .then((response) => {
                    this.updateStep(response.data);
                    return this.loadActivationBrief(true);
                })
                .catch(this.handleHttpError)
                .then(() => {
                    this.isPatchingBrief = false;
                });
        },
        initStep(step) {
            if (step.is_started || get(this, 'brief.status') !== ACTIVATION_BRIEF_STATUS_NONE) {
                return Promise.resolve();
            }

            //this.$bus.$emit(LIVE_CAMPAIGN_PROPOSAL_STEP_EVENTS[step.name].start);

            this.trackLiveUrlEvent(ANALYTICS_PROPOSAL_STEP_EVENTS[step?.name].start, {
                activation_id: this.brief.activation_id
            });

            return this.postStep({
                step: step.name,
                payload: {}
            });
        },
        goNextStep(currentStep, payload) {
            const maxIndex = this.stepsOrder.length - 1;

            this.resetErrors();
            this.isCompletingBrief = true;

            if (!currentStep.is_complete) {
                this.trackLiveUrlEvent(ANALYTICS_PROPOSAL_STEP_EVENTS[currentStep?.name].complete, {
                    activation_id: this.brief.activation_id
                });
            }

            return this.$http
                .patch(this.getStepUrl(currentStep.name, true), payload)
                .then((response) => {
                    this.updateStep(response.data);
                    //this.$bus.$emit(LIVE_CAMPAIGN_PROPOSAL_STEP_EVENTS[currentStep.name].complete);
                    return this.loadActivationBrief(true);
                })
                .then(() => {
                    const maxIndexUpdated = this.stepsOrder.length - 1;
                    const index = this.stepsOrder.findIndex((step) => step.name === currentStep.name);
                    const currentStepUpdated = this.stepsOrder[index];
                    const nextIndex = currentStepUpdated.is_complete ? index + 1 : maxIndexUpdated + 1;
                    const nextStep = this.stepsOrder[nextIndex];

                    if (maxIndex !== maxIndexUpdated) {
                        if (nextStep) {
                            return this.$router.push({ name: STEP_ROUTES[nextStep.name] });
                        }
                    }

                    if (nextIndex <= maxIndexUpdated) {
                        const params = {};

                        if (payload && payload.additional) {
                            params.isAdditionalOrder = true;
                        }

                        return this.$router.push({
                            name: STEP_ROUTES[nextStep.name],
                            params
                        });
                    }

                    return this.$router.push({ name: 'v2.activation.brief.overview' });
                })
                .catch(this.handleHttpError)
                .then(() => {
                    this.isCompletingBrief = false;
                });
        },
        goPreviousStep(currentStep, payload) {
            const index = this.stepsOrder.findIndex((step) => step.name === currentStep.name);
            const data = {
                payload,
                step: currentStep.name,
                complete: true
            };

            if (currentStep.name === 'personal_info') {
                const nextIndex = index - 1;
                const nextStep = this.stepsOrder[nextIndex];
                return this.$router.push({ name: STEP_ROUTES[nextStep.name] });
            }

            return this.patchStep(data).then(() => {
                if (index <= 0) {
                    return this.$router.push({ name: 'v2.activation.brief.overview' });
                }

                const nextIndex = index - 1;
                const nextStep = this.stepsOrder[nextIndex];
                return this.$router.push({ name: STEP_ROUTES[nextStep.name] });
            });
        },
        updateStep(updatedStep) {
            const index = get(this.$root.v2, 'brief.module_progress', []).findIndex(
                (step) => step.name === updatedStep.name
            );

            if (index > -1) {
                this.$root.v2.brief.module_progress.splice(index, 1, updatedStep);
            }
        }
    }
};
